import { SearchResultSkeleton } from "@/src/common/components/GlobalSearchModal/SearchResultSkeleton"
import { useGlobalSearch } from "@/src/contexts/GlobalSearchContext"
import { getNavigationPath } from "@/src/sb/business-layer/factories/URLFactory"
import TooltipV2 from "@/src/sb/components/Tooltip/TooltipV2"
import DatasetIcon from "@/src/sb/components/icon/DatasetIcon"
import GenericSearchIcon from "@/src/sb/components/icon/GenericSearchIcon"
import MetaDataIcon from "@/src/sb/components/icon/MetaDataIcon"
import ReportsIcon from "@/src/sb/components/icon/ReportsIcon"
import {
  ENABLE_LIMITED_PARTNER_PROFILE,
  ENABLE_TRANSACTIONS_PROFILE_V2
} from "@/src/sb/constants/featureFlags"
import { useFeatureValue } from "@growthbook/growthbook-react"
import classNames from "classnames"
import Link from "next/link"
import { useRouter } from "next/router"
import { useCallback, useEffect, useRef } from "react"

export type metaData = {
  [key: string]: string | undefined
}

type SearchResultNavigationTypes = {
  type: string
  slug: string
  bucketSlug?: string
  groupSlug?: string
  tabSlug?: string
  countryRiskSlug?: string
  txnSerialNo?: string
}

export type SearchResultList = {
  id?: number
  name?: string
  metaData?: metaData[]
  type?: string
  slug?: string
  bucketSlug?: string
  groupSlug?: string
  tabSlug?: string
  countryRiskSlug?: string
  txnSerialNo?: string
}
type SearchResultType = {
  searchList?: SearchResultList[]
  isSearchResultsLoading?: boolean
  selectedResultsIndex?: number
  onClickResult?: (result: SearchResultList, index: number) => void
}

const SearchResult = ({
  searchList = [],
  isSearchResultsLoading,
  selectedResultsIndex,
  onClickResult
}: SearchResultType) => {
  const ENTITY_TYPE_CONFIG: Record<
    string,
    { icon: JSX.Element; label: string }
  > = {
    company: { icon: <GenericSearchIcon />, label: "Company" },
    companies: { icon: <GenericSearchIcon />, label: "Company" },
    dataset: { icon: <DatasetIcon />, label: "Dataset" },
    datasets: { icon: <DatasetIcon />, label: "Dataset" },
    report: { icon: <ReportsIcon />, label: "Report" },
    reports: { icon: <ReportsIcon />, label: "Report" },
    transactions: { icon: <GenericSearchIcon />, label: "Transaction" },
    country_profile: { icon: <GenericSearchIcon />, label: "Country" },
    limited_partner: { icon: <GenericSearchIcon />, label: "Limited Partner" }
  }

  const getEntityTypeConfig = (type = "") =>
    ENTITY_TYPE_CONFIG[type.toLowerCase()] ?? {
      icon: <GenericSearchIcon />,
      label: type.replaceAll("_", " ")
    }

  const itemRefs = useRef<(HTMLAnchorElement | null)[]>([])

  const { setLastVisitedUrl } = useGlobalSearch()

  const enableTransactionProfile = useFeatureValue(
    ENABLE_TRANSACTIONS_PROFILE_V2,
    false
  )

  const enableLimitedPartnerProfile = useFeatureValue(
    ENABLE_LIMITED_PARTNER_PROFILE,
    false
  )

  const { push } = useRouter()

  const scrollToSelectedItem = () => {
    if (selectedResultsIndex !== undefined) {
      const selectedItem = itemRefs.current[selectedResultsIndex]
      if (selectedItem) {
        selectedItem.scrollIntoView({
          behavior: "smooth",
          block: "center"
        })
      }
    }
  }

  useEffect(() => {
    scrollToSelectedItem()
  }, [selectedResultsIndex])

  const handleSearchResultsClick = useCallback(
    (data: SearchResultNavigationTypes) => {
      const fullPath = getNavigationPath({
        ...data,
        enableTransactionsProfile: enableTransactionProfile,
        enableLimitedPartnerProfile: enableLimitedPartnerProfile
      })
      push(fullPath, undefined, { shallow: false })
      setLastVisitedUrl(fullPath)
    },
    [searchList, push]
  )

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        if (
          selectedResultsIndex !== undefined &&
          selectedResultsIndex >= 0 &&
          selectedResultsIndex < searchList.length
        ) {
          const currentItem = searchList[selectedResultsIndex]
          handleSearchResultsClick({
            type: currentItem.type as string,
            slug: currentItem.slug as string,
            txnSerialNo: currentItem.txnSerialNo as string,
            ...searchList[selectedResultsIndex]
          })
        }
      }
    }

    document.addEventListener("keydown", handleKeyDown)

    return () => {
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [selectedResultsIndex, searchList])

  return (
    <div>
      {isSearchResultsLoading
        ? Array.from({ length: 5 }).map((_, index) => (
            <SearchResultSkeleton key={index} />
          ))
        : searchList.map((data, index) => {
            const { icon, label } = getEntityTypeConfig(data.type ?? "")
            const stableKey =
              [
                data?.type,
                data?.slug,
                data?.txnSerialNo,
                data?.countryRiskSlug,
                data?.groupSlug,
                data?.bucketSlug,
                data?.id
              ]
                .filter(Boolean)
                .join("|") || `search-item-${index}`
            return (
              <Link
                key={stableKey}
                ref={(el) => (itemRefs.current[index] = el)}
                href={getNavigationPath({
                  type: data.type as string,
                  slug: data.slug as string,
                  ...data,
                  enableTransactionsProfile: enableTransactionProfile,
                  enableLimitedPartnerProfile: enableLimitedPartnerProfile
                })}
                className={classNames(
                  "!flex !flex-row justify-between items-start  border-[#202429] hover:bg-[#1A1D21] font-smoothing-antialiased cursor-pointer !no-underline",
                  {
                    "bg-[#1A1D21]": index === (selectedResultsIndex as number),
                    "py-2.5": index + 1,
                    "mt-2": index === 0,
                    "border-b-[0.25px]": index != searchList?.length - 1
                  }
                )}
                onClick={() => {
                  handleSearchResultsClick({
                    type: data.type as string,
                    slug: data.slug as string,
                    ...data
                  })
                  onClickResult?.(data, index)
                }}
              >
                <div className="flex flex-row items-start gap-2 px-5 w-full">
                  <span className="text-[#9CA3AF] pt-[3px]">{icon}</span>

                  <div className="flex flex-col w-full min-w-0">
                    <TooltipV2
                      content={data.name}
                      className={"md:max-w-[500px]"}
                    >
                      {({ ref, ...props }) => (
                        <span
                          ref={ref}
                          {...props}
                          className="text-xs font-normal font-jakarta !text-[#85BEFF] normal-case tracking-[-0.01em] overflow-hidden whitespace-nowrap text-ellipsis"
                        >
                          {data.name}
                        </span>
                      )}
                    </TooltipV2>

                    <section className="text-[#9CA3AF] text-[10px] capitalize flex flex-wrap gap-1">
                      {data.metaData
                        ?.filter((item) => Object.values(item).some((v) => v))
                        .map((item, index) => {
                          const metaKeys = Object.keys(item).filter(
                            (key) => item[key]
                          )
                          const lastKey = metaKeys[metaKeys.length - 1]

                          return (
                            <span
                              key={index}
                              className="flex items-center gap-1"
                            >
                              {metaKeys.map((key) => (
                                <span
                                  key={key}
                                  className="flex items-center gap-1"
                                >
                                  <TooltipV2 content={item[key]}>
                                    {({ ref, ...props }) => (
                                      <span
                                        ref={ref}
                                        {...props}
                                        className="overflow-hidden whitespace-nowrap text-ellipsis text-[10px] max-w-[120px]"
                                      >
                                        {item[key]}
                                      </span>
                                    )}
                                  </TooltipV2>

                                  {key !== lastKey && <MetaDataIcon />}
                                </span>
                              ))}
                            </span>
                          )
                        })}
                    </section>
                  </div>

                  <p className="text-[#9CA3AF] text-xs capitalize text-nowrap">
                    {label}
                  </p>
                </div>
              </Link>
            )
          })}
    </div>
  )
}

export default SearchResult
